import { useState } from "react";
import { useT } from "../../i18n/index.js";
import { trpc } from "../../lib/trpc.js";

/**
 * F-tech-signup (CEO 2026-09-20) — fila do auto-cadastro. Candidatura não é
 * conta: aprovar aqui é que cria o usuário (no idioma que ele escolheu), o
 * vínculo com a empresa e o cadastro de técnico, com o prefixo da ordem
 * gerado sem colidir. Empresa e função são decisão do escritório.
 */
export function TechnicianApplications({ companyId }: { companyId: number | null }) {
  const t = useT();
  const utils = trpc.useUtils();
  const [verTodas, setVerTodas] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<string | null>(null);
  const [funcao, setFuncao] = useState<Record<number, "debosselage" | "montage">>({});
  /**
   * F-tech-signup-multi (CEO 2026-09-21) — o mesmo técnico costuma trabalhar
   * para as DUAS empresas. Aprovar só na empresa em que se está logado
   * obrigava a refazer tudo do outro lado, e a tela de Usuários ainda barrava
   * a função por falta de cadastro de técnico lá. Aqui se escolhe quais.
   */
  const [empresas, setEmpresas] = useState<Record<number, number[]>>({});

  const q = trpc.technicianApplications.list.useQuery({
    status: verTodas ? "all" : "pending",
    limit: 100,
  });
  const companiesQuery = trpc.companies.list.useQuery();
  const companies = companiesQuery.data ?? [];
  const escolhidas = (appId: number): number[] =>
    empresas[appId] ?? (companyId != null ? [companyId] : []);

  const aprovar = trpc.technicianApplications.approve.useMutation({
    onSuccess: (r) => {
      setErro(null);
      setFeito(
        `${t("technicians.appApproved")} — ${r.prefixo} (${t("technicians.appPrefixMade")})`,
      );
      void utils.technicianApplications.invalidate();
      void utils.technicians.invalidate();
    },
    onError: (e) => setErro(e.message),
  });
  const recusar = trpc.technicianApplications.reject.useMutation({
    onSuccess: () => {
      setErro(null);
      void utils.technicianApplications.invalidate();
    },
    onError: (e) => setErro(e.message),
  });

  const linhas = q.data?.rows ?? [];
  const pendendo = aprovar.isPending || recusar.isPending;

  return (
    <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-neutral-500">{t("technicians.appsHint")}</p>
        <button
          type="button"
          onClick={() => setVerTodas((v) => !v)}
          className="rounded-md border border-neutral-700 px-3 py-1 text-xs hover:border-lime"
        >
          {verTodas ? t("technicians.appShowPending") : t("technicians.appShowAll")}
        </button>
      </div>

      {erro && (
        <p className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {erro}
        </p>
      )}
      {feito && (
        <p className="rounded-md border border-lime/30 bg-lime/10 px-3 py-2 text-sm text-lime">
          {feito}
        </p>
      )}

      {linhas.length === 0 ? (
        <p className="text-sm text-neutral-600">{t("technicians.appsEmpty")}</p>
      ) : (
        <div className="space-y-2">
          {linhas.map((a) => {
            const end = (a.address ?? null) as {
              street?: string;
              postalCode?: string;
              city?: string;
            } | null;
            const escolhida = funcao[a.id] ?? (a.serviceRole as "debosselage" | "montage");
            return (
              <div
                key={a.id}
                className="rounded-lg border border-neutral-800 bg-neutral-950/60 p-3 space-y-2"
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-semibold">{a.name}</span>
                  <span className="text-sm text-neutral-400">{a.email}</span>
                  <span className="text-sm text-neutral-500">· {a.phone}</span>
                  <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] uppercase text-neutral-400">
                    {a.locale}
                  </span>
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] ${
                      a.status === "pending"
                        ? "bg-amber-500/15 text-amber-300"
                        : a.status === "approved"
                          ? "bg-lime/15 text-lime"
                          : "bg-red-500/15 text-red-300"
                    }`}
                  >
                    {a.status === "pending"
                      ? t("technicians.appPending")
                      : a.status === "approved"
                        ? t("technicians.appApproved")
                        : t("technicians.appRejected")}
                  </span>
                </div>

                <p className="text-xs text-neutral-500">
                  {a.countryCode}
                  {a.taxId ? ` · ${a.taxIdKind} ${a.taxId}` : ""}
                  {end?.street ? ` · ${end.street}` : ""}
                  {end?.postalCode || end?.city
                    ? ` · ${[end.postalCode, end.city].filter(Boolean).join(" ")}`
                    : ""}
                </p>
                {a.jaExiste && (
                  <p className="rounded-md border border-sky-500/30 bg-sky-500/10 px-2 py-1.5 text-xs text-sky-200">
                    ♻ {t("technicians.appExistsPre")} <strong>{a.jaExiste.name}</strong>
                    {a.jaExiste.orderPrefix ? ` (${a.jaExiste.orderPrefix})` : ""}
                    {a.jaExiste.dossiers > 0
                      ? ` · ${a.jaExiste.dossiers} ${t("technicians.appExistsCars")}`
                      : ""}{" "}
                    {t("technicians.appExistsSuf")}
                  </p>
                )}
                {a.note && <p className="text-xs italic text-neutral-400">“{a.note}”</p>}
                {a.rejectionReason && <p className="text-xs text-red-300">{a.rejectionReason}</p>}

                {a.status === "pending" && (
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <label className="text-xs text-neutral-500">
                      {t("technicians.appRoleLabel")}
                    </label>
                    <select
                      value={escolhida}
                      onChange={(e) =>
                        setFuncao((f) => ({
                          ...f,
                          [a.id]: e.target.value as "debosselage" | "montage",
                        }))
                      }
                      className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs"
                    >
                      <option value="debosselage">{t("technicians.appRolePdr")}</option>
                      <option value="montage">{t("technicians.appRoleMontador")}</option>
                    </select>
                    {companies.length > 1 && (
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs text-neutral-500">
                          {t("technicians.appCompaniesLabel")}
                        </span>
                        {companies.map((c) => {
                          const marcada = escolhidas(a.id).includes(c.id);
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() =>
                                setEmpresas((prev) => {
                                  const atual = prev[a.id] ?? escolhidas(a.id);
                                  return {
                                    ...prev,
                                    [a.id]: marcada
                                      ? atual.filter((x) => x !== c.id)
                                      : [...atual, c.id],
                                  };
                                })
                              }
                              className={`rounded-md border px-2 py-1 text-xs ${
                                marcada
                                  ? "border-lime bg-lime/15 text-lime"
                                  : "border-neutral-700 text-neutral-400 hover:border-lime"
                              }`}
                            >
                              {marcada ? "✓ " : ""}
                              {c.name}
                            </button>
                          );
                        })}
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={pendendo || escolhidas(a.id).length === 0}
                      onClick={() => {
                        const alvos = escolhidas(a.id);
                        if (alvos.length === 0) return;
                        aprovar.mutate({
                          companyIds: alvos,
                          applicationId: a.id,
                          serviceRole: escolhida,
                        });
                      }}
                      className="rounded-md bg-lime px-3 py-1 text-xs font-semibold text-on-lime disabled:opacity-40"
                    >
                      {t("technicians.appApproveBtn")}
                    </button>
                    <button
                      type="button"
                      disabled={pendendo}
                      onClick={() => {
                        const motivo = window.prompt(t("technicians.appRejectPrompt"));
                        if (motivo && motivo.trim().length >= 3) {
                          recusar.mutate({ applicationId: a.id, reason: motivo.trim() });
                        }
                      }}
                      className="rounded-md border border-neutral-700 px-3 py-1 text-xs hover:border-red-400 hover:text-red-300 disabled:opacity-40"
                    >
                      {t("technicians.appRejectBtn")}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
